/**
 * OperationalRules Repository
 * 운영 규칙 API 호출 계층
 */

import { API_BASE_URL } from '../../../config/api';
import type { OperationalSettings, SimulationResult } from '../models/OperationalRules';

export class OperationalRulesRepository {
  private async apiCall<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken') || 'demo-token';
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * 운영 규칙 조회
   */
  async getRules(): Promise<OperationalSettings> {
    return this.apiCall<OperationalSettings>('/api/v1/rules');
  }

  /**
   * 운영 규칙 업데이트
   */
  async updateRules(rules: Partial<OperationalSettings>): Promise<OperationalSettings> {
    return this.apiCall<OperationalSettings>('/api/v1/rules', {
      method: 'PUT',
      body: JSON.stringify(rules),
    });
  }

  /**
   * 자동 퇴실 시뮬레이션 실행
   */
  async simulateAutoCheckout(sessionEndTime: Date, gracePeriod: number): Promise<SimulationResult> {
    return this.apiCall<SimulationResult>('/api/v1/rules/simulate/auto-checkout', {
      method: 'POST',
      body: JSON.stringify({
        session_end_time: sessionEndTime.toISOString(),
        grace_period_minutes: gracePeriod,
      }),
    });
  }

  /**
   * 예약 시뮬레이션 실행
   */
  async simulateReservation(reservationTime: Date, advanceHours: number): Promise<SimulationResult> {
    return this.apiCall<SimulationResult>('/api/v1/rules/simulate/reservation', {
      method: 'POST',
      body: JSON.stringify({
        reservation_time: reservationTime.toISOString(),
        max_advance_hours: advanceHours,
      }),
    });
  }

  /**
   * 기본값으로 초기화
   */
  async resetToDefaults(): Promise<OperationalSettings> {
    return this.apiCall<OperationalSettings>('/api/v1/rules/reset', {
      method: 'POST',
    });
  }
}

export const operationalRulesRepository = new OperationalRulesRepository();
